import { gsap } from 'gsap';

export default class HeadlineAnimations {
    constructor() {
        this.headlines = [];
        this.timelines = [];
        this.isMobile = window.innerWidth <= 768;

        // Scroll direction tracking
        this.lastScrollY = window.scrollY;
        this.scrollDirection = 'down'; // 'up' or 'down'
        this.isScrolling = false;
        this.scrollTimeout = null;

        this.init();
        this.setupResizeListener();
        this.setupScrollListener();
    }

    init() {
        // Find all headline elements
        const headlineElements = document.querySelectorAll('.headline-text');

        headlineElements.forEach((element, index) => {
            this.createScrollingAnimation(element, index);
        });
    }

    createScrollingAnimation(element, index) {
        // Create timeline for infinite scrolling
        const timeline = gsap.timeline({
            repeat: -1,
            ease: "none"
        });

        // Different durations for desktop vs mobile
        const duration = this.isMobile ? 20 : 30;

        // Start with default direction (left - negative X)
        timeline.to(element, {
            x: "-50%",
            duration: duration,
            ease: "none"
        });

        // Store references
        this.headlines.push(element);
        this.timelines.push(timeline);

        return timeline;
    }

    setupScrollListener() {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    this.onScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
    }

    onScroll() {
        const currentScrollY = window.scrollY;
        const newDirection = currentScrollY > this.lastScrollY ? 'down' : 'up';

        // Only update if direction changed
        if (newDirection !== this.scrollDirection) {
            this.scrollDirection = newDirection;
            this.updateAnimationDirection();
        }

        this.lastScrollY = currentScrollY;

        // Set scrolling state
        this.isScrolling = true;
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
            this.isScrolling = false;
        }, 150);
    }

    updateAnimationDirection() {
        this.timelines.forEach((timeline, index) => {
            if (this.scrollDirection === 'down') {
                // Scrolling down: normal direction (forward)
                timeline.timeScale(1);
            } else {
                // Scrolling up: reverse direction (backward)
                timeline.timeScale(-1);
            }
        });
    }

    setupResizeListener() {
        window.addEventListener('resize', () => {
            const wasMobile = this.isMobile;
            this.isMobile = window.innerWidth <= 768;

            // If mobile state changed, recreate animations with new timing
            if (wasMobile !== this.isMobile) {
                this.recreateAnimations();
            }
        });
    }

    recreateAnimations() {
        // Kill existing timelines
        this.timelines.forEach(timeline => timeline.kill());
        this.timelines = [];

        // Recreate with new timing
        this.headlines.forEach((element, index) => {
            // Reset position first
            gsap.set(element, { x: "0%" });
            this.createScrollingAnimation(element, index);
        });
    }

    // Method to pause all headline animations
    pauseAll() {
        this.timelines.forEach(timeline => timeline.pause());
    }

    // Method to resume all headline animations
    resumeAll() {
        this.timelines.forEach(timeline => timeline.resume());
    }

    // Method to set speed multiplier for all animations
    setSpeed(multiplier = 1) {
        this.timelines.forEach(timeline => timeline.timeScale(multiplier));
    }

    // Cleanup method
    destroy() {
        this.timelines.forEach(timeline => timeline.kill());
        window.removeEventListener('resize', this.setupResizeListener);
        window.removeEventListener('scroll', this.onScroll);
        clearTimeout(this.scrollTimeout);
        this.headlines = [];
        this.timelines = [];
    }
}